import type { ChartCandle } from '../chart.typings'
import {
    type IndicatorWorkerRequest,
    type IndicatorWorkerResponse,
    type RecordedIndicatorCall,
} from './indicatorWorker.types'

/** A script that hangs this long is treated as unsupported and the worker is recycled. */
const EVALUATION_TIMEOUT_MS = 3000

type PendingEvaluation = {
    script: string
    resolve: (calls: RecordedIndicatorCall[] | null) => void
    timer: ReturnType<typeof setTimeout>
}

let worker: Worker | null | undefined
/** False until the worker answers once: timeouts before that are module loading, not a slow script. */
let workerResponded = false
let nextId = 1
const pending = new Map<number, PendingEvaluation>()
/** Scripts that failed in the worker once (network, cross-indicator inputs, errors) stay on the main thread. */
const mainThreadScripts = new Set<string>()

function settle(id: number, calls: RecordedIndicatorCall[] | null, keepOnMainThread = !calls) {
    const entry = pending.get(id)
    if (!entry) return
    pending.delete(id)
    clearTimeout(entry.timer)
    if (keepOnMainThread) {
        mainThreadScripts.add(entry.script)
    }
    entry.resolve(calls)
}

function resetWorker() {
    worker?.terminate()
    worker = undefined
    workerResponded = false
    // Other in-flight scripts did nothing wrong: fall back for this tick only.
    Array.from(pending.keys()).forEach((id) => settle(id, null, false))
}

function getWorker(): Worker | null {
    if (worker !== undefined) return worker
    try {
        worker = new Worker(new URL('./indicatorScript.worker.ts', import.meta.url), { type: 'module' })
        worker.onmessage = (event: MessageEvent<IndicatorWorkerResponse>) => {
            const response = event.data
            workerResponded = true
            settle(response.id, response.ok ? response.calls : null)
        }
        worker.onerror = () => {
            // The worker failed to load or crashed: run everything on the main thread from now on.
            resetWorker()
            worker = null
        }
    } catch {
        worker = null
    }
    return worker
}

/** Primitive-valued fields of the first and last candle (fields can appear mid-history, e.g. OI or greeks). */
function candleFields(candles: ChartCandle[]): string[] {
    const fields = new Set<string>()
    for (const candle of [candles[0], candles[candles.length - 1]]) {
        if (!candle) continue
        for (const [field, value] of Object.entries(candle)) {
            if (value === null || value === undefined || typeof value !== 'object') {
                fields.add(field)
            }
        }
    }
    return Array.from(fields)
}

export function canEvaluateIndicatorInWorker(script: string): boolean {
    return !mainThreadScripts.has(script) && !script.includes('series.fetch') && getWorker() !== null
}

/**
 * Runs `script` in the indicator worker. Resolves with the recorded plot calls, or null when the script must run
 * on the main thread instead (the caller then falls back to createIndicatorData).
 */
export function evaluateIndicatorInWorker(
    params: Omit<IndicatorWorkerRequest, 'id' | 'columns'> & { candles: ChartCandle[] }
): Promise<RecordedIndicatorCall[] | null> {
    const target = getWorker()
    if (!target) return Promise.resolve(null)
    const { candles, ...rest } = params
    const columns: IndicatorWorkerRequest['columns'] = {}
    for (const field of candleFields(candles)) {
        columns[field] = candles.map((candle) => (candle as unknown as Record<string, unknown>)[field])
    }
    const id = nextId++
    return new Promise((resolve) => {
        const timer = setTimeout(() => {
            if (!workerResponded) {
                // Still loading: run this tick on the main thread and keep the worker.
                settle(id, null, false)
                return
            }
            settle(id, null)
            resetWorker()
        }, EVALUATION_TIMEOUT_MS)
        pending.set(id, { script: params.script, resolve, timer })
        try {
            target.postMessage({ ...rest, id, columns } satisfies IndicatorWorkerRequest)
        } catch {
            settle(id, null)
        }
    })
}
